import { QUERY_PARAM_PARENT_PRODUCT } from '@canvas/constants/query-params';
import { contentAPILocal } from '@canvas/frame-api-devloop/canvas-frame-api-local';
import { isLocalEAPDevMode, isOfflineMode } from '@canvas/frame-api-devloop/is-dev-mode';
import { NoopContentAPI } from '@canvas/frame-api-noop';
import {
  type ContentWindowApi,
  type ContentAPI,
  type ContentAPIFactory,
} from '@canvas/frame-api-types';

import { getEmbedSearchParams } from '../canvas-embed-params';
import {
  CanvasContentAPI,
  isAuthlessOnlineMode,
  isFrameAPI,
  isLocalEAPTestMode,
} from '../canvas-frame-api';

import { SmartLinkAPI, NativeAPI, OnlineAPI } from './host-window-api';

const inLocalEAPMode = isLocalEAPDevMode() || isLocalEAPTestMode();

export const getContentAPI = (): ContentAPI | undefined => {
  try {
    // eslint-disable-next-line no-restricted-syntax
    return getFrameApi().contentAPI;
  } catch (err) {
    console.error(err);
  }
};

export let canvasFrameAPI: ContentAPI | undefined = undefined;

let contentAPIPromise: Promise<ContentAPI> | null = null;

/**
 * This is a wrapper around getFrameApi() that returns a promise.
 * It is used to wait for the contentAPI to be available.
 *
 * Use this instead of getContentAPI() in cases where the application is loaded before
 * the host frame API is ready.
 *
 * An example of when it's needed is when the databases iframe is loaded by SSR
 * response and the application loads before Confluence contentAPI is available.
 *
 * @returns A promise that resolves to the contentAPI.
 */
export const waitForContentAPI = (): Promise<ContentAPI> => {
  if (!contentAPIPromise) {
    contentAPIPromise = new Promise((resolve) => {
      // Check if contentAPI is available, polling each frame until it is ready
      const checkForContentAPI = () => {
        let api: ContentAPI | undefined;

        try {
          // eslint-disable-next-line no-restricted-syntax
          api = getFrameApi().contentAPI;
        } catch (err) {
          console.error(err);
        }

        if (api) {
          // Resolve once we have contentAPI, not scheduling any more frames
          canvasFrameAPI = api;
          resolve(api);
        } else {
          // Try again on the next frame
          requestAnimationFrame(checkForContentAPI);
        }
      };

      // Start the first check
      checkForContentAPI();
    });
  }

  return contentAPIPromise;
};

const getRawContentApiOverride = (rawContentApi: unknown) => {
  if (isOfflineMode()) {
    return new NoopContentAPI();
  }

  if (isFrameAPI(rawContentApi)) {
    return rawContentApi;
  }

  return contentAPILocal;
};

export const getFrameApi = (): ContentWindowApi => {
  // Adding a fallback here to satisfy unit tests where jsdom hasn't been set up yet
  const href = globalThis.location?.href ?? 'ws://.';
  const url = new URL(href);

  const containerAri = url.searchParams.get('containerAri');
  const boardAri = url.searchParams.get('boardAri');

  const embedded = url.searchParams.get(QUERY_PARAM_PARENT_PRODUCT) === 'smartLink';

  const isSmartLink = embedded && Boolean(containerAri) && Boolean(boardAri);

  let contentAPIOverride: ContentAPIFactory | undefined = undefined;

  if (inLocalEAPMode) {
    contentAPIOverride = (rawContentApi) => {
      return new CanvasContentAPI(getRawContentApiOverride(rawContentApi));
    };
  }

  if (isSmartLink) {
    return new SmartLinkAPI(contentAPIOverride);
  }

  if (isAuthlessOnlineMode()) {
    return new OnlineAPI(contentAPIOverride);
  }

  return new NativeAPI(contentAPIOverride);
};

export { type ContentAPI } from '@canvas/frame-api-types';

const embedSearchParams = getEmbedSearchParams();

export const isEmbeddedSmartLink = () => {
  const { parentProduct } = embedSearchParams;

  return parentProduct === 'smartLink';
};

export const shouldHideBorder = () => embedSearchParams.hideBorder === true;

export const isRovoPreview = () => {
  const { parentProduct, embed } = embedSearchParams;

  return parentProduct === 'rovo' && embed !== true;
};

export const isRovoEmbed = () => {
  const { parentProduct, embed } = embedSearchParams;

  return parentProduct === 'rovo' && embed === true;
};

export const isRendererPreview = () => {
  return !!(window as any).previewAPI;
};
